import { useState, useEffect, useRef, Component } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Search, Package, Trash2, DollarSign, AlertCircle, Pencil, RotateCcw, Shield, Printer, CheckCircle2, Loader2, IdCard } from 'lucide-react';
import PatternPad from '../components/PatternPad';
import ThermalTicket from '../components/ThermalTicket';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  available: { label: 'Disponible', color: 'bg-blue-100 text-blue-700' },
  waiting_part: { label: 'Esperando pieza', color: 'bg-amber-100 text-amber-700' },
  waiting_client: { label: 'Esperando cliente', color: 'bg-purple-100 text-purple-700' },
  outsourcing: { label: 'Servicio externo', color: 'bg-indigo-100 text-indigo-700' },
  liberacion: { label: 'Liberacion', color: 'bg-cyan-100 text-cyan-700' },
  archived: { label: 'Archivado', color: 'bg-slate-100 text-slate-700' },
  done: { label: 'Terminado', color: 'bg-green-100 text-green-700' },
  delivered: { label: 'Entregado', color: 'bg-emerald-100 text-emerald-700' },
};

// Safe numeric formatter — accepts numbers, numeric strings, null/undefined,
// NaN, Infinity. Never throws. Returns "0.00" for anything unusable.
const n2 = (val) => {
  if (val === null || val === undefined || val === '') return '0.00';
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
};

// Safe date formatter — returns '—' for invalid/missing values.
const dateStr = (val, opts) => {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return '—';
    return opts === 'short' ? d.toLocaleDateString('es-MX') : d.toLocaleString('es-MX');
  } catch {
    return '—';
  }
};

// Page-level error boundary so a single bad field never blanks out the entire
// detail view. Renders a friendly fallback with a reload action instead.
class RepairDetailBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error('RepairDetail render error:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div data-testid="repair-detail-error" className="max-w-xl mx-auto mt-8">
          <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#1D3C6E] mb-4">
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
          <div className="bg-white rounded-lg border border-rose-200 p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-rose-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-slate-900 font-['Outfit']">No se pudo mostrar la reparación</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Esta reparación tiene datos faltantes o malformados. Puedes regresar al listado o intentar recargar la página.
                </p>
                <p className="text-[11px] text-slate-400 mt-2 font-mono break-all">{String(this.state.error?.message || this.state.error)}</p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => window.location.reload()}>Recargar</Button>
                  <Button size="sm" className="bg-[#1D3C6E] hover:bg-[#152B50]" onClick={() => window.location.href = '/repairs'}>Volver al listado</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function RepairDetailInner() {
  const { id } = useParams();
  const { getAuthHeaders, currentBranch } = useAuth();
  const navigate = useNavigate();
  const [repair, setRepair] = useState(null);
  const [branches, setBranches] = useState([]);
  const [models, setModels] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [waitTimeOptions, setWaitTimeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusError, setStatusError] = useState('');
  // Items
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryResults, setInventoryResults] = useState([]);
  const [itemSourceBranch, setItemSourceBranch] = useState('');
  // Payment
  const [showPayment, setShowPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  // Refund
  const [showRefund, setShowRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState('Efectivo');
  const [refundReason, setRefundReason] = useState('');
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  // Bolsa availability check (edit dialog)
  const [bagCheck, setBagCheck] = useState({ status: 'idle', message: '' });
  const bagDebounceRef = useRef(null);
  // Security code verification for delivery
  const [showDeliveryVerify, setShowDeliveryVerify] = useState(false);
  const [securityCodeInput, setSecurityCodeInput] = useState('');
  // Delivery verification method: 'security_code' | 'id_document'
  const [deliveryMethod, setDeliveryMethod] = useState('security_code');
  const [idName, setIdName] = useState('');
  const [idLastName, setIdLastName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [deliveryError, setDeliveryError] = useState('');

  useEffect(() => { fetchRepair(); fetchBranches(); fetchModels(); fetchChecklist(); fetchWaitTimes(); }, [id]);

  // Register-open state for the repair's branch — drives payment/refund gating.
  const [registerOpen, setRegisterOpen] = useState(true);
  // Note modal + inline editing of Nota log entries
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingLogId, setEditingLogId] = useState(null);
  const [editingLogText, setEditingLogText] = useState('');
  // Solicitar Pieza modal
  const [piezaOpen, setPiezaOpen] = useState(false);
  const [piezaSearch, setPiezaSearch] = useState('');
  const [piezaSuggestions, setPiezaSuggestions] = useState([]);
  const [piezaSearchLoading, setPiezaSearchLoading] = useState(false);
  const [piezaSelected, setPiezaSelected] = useState(null); // inventory item selected from search
  const [piezaQty, setPiezaQty] = useState('1');
  const [piezaMode, setPiezaMode] = useState('search'); // 'search' | 'manual'
  const [piezaManual, setPiezaManual] = useState({ name: '', modelo: '', color: '', cantidad: '1' });
  const [piezaSaving, setPiezaSaving] = useState(false);
  useEffect(() => {
    if (!repair?.branch_id) { setRegisterOpen(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/cash-register/status?branch_id=${repair.branch_id}`, { headers: getAuthHeaders() });
        if (!cancelled) setRegisterOpen(!!r.data?.is_open);
      } catch { if (!cancelled) setRegisterOpen(true); }
    })();
    return () => { cancelled = true; };
  }, [repair?.branch_id, repair?.total_paid]);

  const fetchRepair = async () => {
    try {
      const res = await axios.get(`${API}/api/repairs/${id}`, { headers: getAuthHeaders() });
      setRepair(res.data);
    } catch { navigate('/repairs'); }
    setLoading(false);
  };

  const fetchBranches = async () => { try { const r = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() }); setBranches(r.data); } catch {} };
  const fetchModels = async () => { try { const r = await axios.get(`${API}/api/settings/compatible_models`, { headers: getAuthHeaders() }); setModels((r.data?.value || []).sort((a, b) => a.localeCompare(b))); } catch {} };
  const fetchChecklist = async () => { try { const r = await axios.get(`${API}/api/settings/repair_checklist`, { headers: getAuthHeaders() }); setChecklistItems(r.data?.value || []); } catch {} };
  const fetchWaitTimes = async () => { try { const r = await axios.get(`${API}/api/settings/wait_times`, { headers: getAuthHeaders() }); setWaitTimeOptions(r.data?.value || []); } catch {} };

  // --- Solicitar Pieza: debounced inventory search ---
  useEffect(() => {
    if (!piezaOpen || piezaMode !== 'search') { setPiezaSuggestions([]); return; }
    const q = piezaSearch.trim();
    if (q.length < 2) { setPiezaSuggestions([]); return; }
    const timer = setTimeout(async () => {
      setPiezaSearchLoading(true);
      try {
        const r = await axios.get(`${API}/api/inventory?search=${encodeURIComponent(q)}`, { headers: getAuthHeaders() });
        setPiezaSuggestions((r.data || []).slice(0, 10));
      } catch (err) { console.error(err); }
      finally { setPiezaSearchLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [piezaSearch, piezaOpen, piezaMode, getAuthHeaders]);

  const submitSolicitarPieza = async () => {
    setPiezaSaving(true);
    try {
      let payload;
      let logDetail;
      if (piezaMode === 'search') {
        if (!piezaSelected) { alert('Selecciona un artículo del inventario'); setPiezaSaving(false); return; }
        const qty = parseInt(piezaQty) || 1;
        if (qty <= 0) { alert('La cantidad debe ser mayor a 0'); setPiezaSaving(false); return; }
        const inv = piezaSelected;
        payload = {
          type: 'inv',
          item_id: inv.id,
          branch_id: repair?.branch_id || '',
          cantidad: qty,
          repair_id: id,
          repair_ticket: repair?.ticket || '',
        };
        logDetail = `Pieza Solicitada: ${inv.name}${inv.sku ? ` (SKU: ${inv.sku})` : ''} · Cant. ${qty}`;
      } else {
        const name = piezaManual.name.trim();
        if (!name) { alert('Ingresa el nombre de la pieza'); setPiezaSaving(false); return; }
        const qty = parseInt(piezaManual.cantidad) || 1;
        if (qty <= 0) { alert('La cantidad debe ser mayor a 0'); setPiezaSaving(false); return; }
        payload = {
          type: 'custom',
          name,
          modelo: piezaManual.modelo.trim(),
          color: piezaManual.color.trim(),
          branch_id: repair?.branch_id || '',
          cantidad: qty,
          repair_id: id,
          repair_ticket: repair?.ticket || '',
        };
        const extras = [piezaManual.modelo.trim(), piezaManual.color.trim()].filter(Boolean).join(' / ');
        logDetail = `Pieza Solicitada: ${name}${extras ? ` (${extras})` : ''} · Cant. ${qty}`;
      }

      // 1) Persist the line in the Pedidos draft (DB-backed cart)
      await axios.post(`${API}/api/purchase-orders/draft`, payload, { headers: getAuthHeaders() });
      // 2) Add a log entry in the repair bitácora — reference link
      await axios.post(`${API}/api/repairs/${id}/logs`,
        { description: logDetail },
        { headers: getAuthHeaders() });
      setPiezaOpen(false);
      fetchRepair();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo solicitar la pieza');
    } finally { setPiezaSaving(false); }
  };


  const round2 = (n) => Math.round(n * 100) / 100;
  const getBalance = () => {
    const total = parseFloat(repair?.total_cost) || 0;
    const paid = parseFloat(repair?.total_paid) || 0;
    return Math.max(0, round2(total - paid));
  };

  const updateStatus = async (newStatus) => {
    setStatusError('');
    // Intercept "delivered" — require verification (claim code or ID document)
    if (newStatus === 'delivered') {
      setShowDeliveryVerify(true);
      setSecurityCodeInput('');
      setIdName(''); setIdLastName(''); setIdNumber('');
      setDeliveryMethod('security_code');
      setDeliveryError('');
      return;
    }
    try {
      const res = await axios.put(`${API}/api/repairs/${id}`, { status: newStatus }, { headers: getAuthHeaders() });
      setRepair(res.data);
    } catch (err) { setStatusError(err.response?.data?.detail || 'Error al cambiar estado'); }
  };

  const confirmDelivery = async () => {
    setDeliveryError('');
    setStatusError('');
    const payload = { status: 'delivered', verification_method: deliveryMethod };
    if (deliveryMethod === 'security_code') {
      if (securityCodeInput.length !== 6) {
        setDeliveryError('Ingresa los 6 dígitos del código');
        return;
      }
      payload.security_code = securityCodeInput;
    } else {
      if (!idName.trim() || !idLastName.trim() || !idNumber.trim()) {
        setDeliveryError('Nombre, apellido y número de identificación son obligatorios');
        return;
      }
      payload.id_name = idName.trim();
      payload.id_last_name = idLastName.trim();
      payload.id_number = idNumber.trim();
    }
    try {
      const res = await axios.put(`${API}/api/repairs/${id}`, payload, { headers: getAuthHeaders() });
      setRepair(res.data);
      setShowDeliveryVerify(false);
      setSecurityCodeInput('');
      setIdName(''); setIdLastName(''); setIdNumber('');
    } catch (err) {
      setDeliveryError(err.response?.data?.detail || 'Error al verificar la entrega');
    }
  };

  const searchInventory = async () => {
    if (!inventorySearch.trim()) return;
    try {
      const res = await axios.get(`${API}/api/inventory?search=${encodeURIComponent(inventorySearch)}`, { headers: getAuthHeaders() });
      setInventoryResults(res.data);
    } catch {}
  };

  const addItem = async (inv) => {
    try {
      const res = await axios.post(`${API}/api/repairs/${id}/items`, {
        inventory_id: inv.id, quantity: 1, source_branch_id: itemSourceBranch || currentBranch || ''
      }, { headers: getAuthHeaders() });
      setRepair(res.data);
      setInventoryResults([]);
      setInventorySearch('');
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const removeItem = async (itemId) => {
    try { const res = await axios.delete(`${API}/api/repairs/${id}/items/${itemId}`, { headers: getAuthHeaders() }); setRepair(res.data); } catch {}
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/repairs/${id}/payments`, { amount: parseFloat(paymentAmount), method: paymentMethod }, { headers: getAuthHeaders() });
      setRepair(res.data);
      setShowPayment(false);
      setPaymentAmount('');
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleRefund = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/repairs/${id}/refund`, { amount: parseFloat(refundAmount), method: refundMethod, reason: refundReason }, { headers: getAuthHeaders() });
      setRepair(res.data);
      setShowRefund(false);
      setRefundAmount('');
      setRefundReason('');
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

// Checklist 4-state options (Sí · No · No puedo revisar · Roto pero funcional)
const CHECKLIST_OPTIONS = [
  { value: 'si', label: 'Sí', shortLabel: 'Sí', activeCls: 'bg-emerald-600 text-white border-emerald-600' },
  { value: 'no', label: 'No', shortLabel: 'No', activeCls: 'bg-rose-600 text-white border-rose-600' },
  { value: 'cant_check', label: 'No puedo revisar', shortLabel: 'No revisable', activeCls: 'bg-slate-600 text-white border-slate-600' },
  { value: 'broken_works', label: 'Roto Pero Funcional', shortLabel: 'Roto/funcional', activeCls: 'bg-amber-500 text-white border-amber-500' },
];

// Normalise checklist data: legacy `true` → 'si', anything falsy → null
const normalizeChecklist = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v === true) out[k] = 'si';
    else if (v === false || v == null || v === '') out[k] = null;
    else out[k] = String(v);
  }
  return out;
};

// Parse a log-entry description into a (evento, detalle) pair for the
// 4-column Bitácora table. Backend descriptions follow the patterns produced
// by `_log_event` in routes/repairs.py — keep this map aligned with them.
const CLIENT_FIELD_LABELS = new Set([
  'nombre del cliente', 'teléfono del cliente', 'teléfono de contacto',
]);
const DEVICE_FIELD_LABELS = new Set([
  'marca del equipo', 'modelo del equipo', 'descripción del problema',
  'presupuesto', 'costo final', 'tiempo de espera', 'técnico',
]);
const parseLogEntry = (desc) => {
  const d = String(desc || '').trim();
  if (!d) return { evento: '—', detalle: '' };
  // "Prefijo: detalle" matches
  const COLON_PREFIXES = [
    { re: /^Cambio de estado:\s*(.+)$/i,           evento: 'Cambio de estado' },
    { re: /^Traslado de sucursal:\s*(.+)$/i,       evento: 'Cambio de sucursal' },
    { re: /^Bolsa actualizada:\s*(.+)$/i,          evento: 'Cambio de bolsa' },
    { re: /^Artículo agregado:\s*(.+)$/i,          evento: 'Artículo agregado' },
    { re: /^Artículo eliminado:\s*(.+)$/i,         evento: 'Artículo borrado' },
    { re: /^Pago registrado:\s*(.+)$/i,            evento: 'Pago registrado' },
    { re: /^Reembolso registrado:\s*(.+)$/i,       evento: 'Reembolso' },
    { re: /^Nota:\s*(.+)$/i,                       evento: 'Nota' },
  ];
  for (const { re, evento } of COLON_PREFIXES) {
    const m = d.match(re);
    if (m) return { evento, detalle: m[1].trim() };
  }
  // "Se actualizó {campo}" → Info cliente / Info equipo editada
  const upd = d.match(/^Se actualizó\s+(.+)$/i);
  if (upd) {
    const field = upd[1].trim().toLowerCase();
    if (CLIENT_FIELD_LABELS.has(field)) return { evento: 'Info cliente editada', detalle: upd[1].trim() };
    if (DEVICE_FIELD_LABELS.has(field))  return { evento: 'Info equipo editada',  detalle: upd[1].trim() };
    return { evento: 'Edición', detalle: upd[1].trim() };
  }
  // Equipo entregado
  const del = d.match(/^Equipo entregado al cliente\s*\((.+)\)$/i);
  if (del) return { evento: 'Entrega', detalle: del[1].trim() };
  // Reparación creada
  const created = d.match(/^Reparación creada\s*\((.+)\)$/i);
  if (created) return { evento: 'Creación', detalle: created[1].trim() };
  // Otros eventos sin detalle explícito
  const FLAT = [
    { re: /^Checklist de inspección actualizado$/i,    evento: 'Info equipo editada', detalle: 'Checklist' },
    { re: /^Clave de desbloqueo actualizada$/i,        evento: 'Info equipo editada', detalle: 'Clave de desbloqueo' },
    { re: /^Patrón de desbloqueo actualizado$/i,       evento: 'Info equipo editada', detalle: 'Patrón de desbloqueo' },
    { re: /^Nota agregada$/i,                          evento: 'Nota',                detalle: '' },
  ];
  for (const { re, evento, detalle } of FLAT) {
    if (re.test(d)) return { evento, detalle };
  }
  // Fallback: split on first ":" if present
  const colon = d.indexOf(':');
  if (colon > 0) return { evento: d.slice(0, colon).trim(), detalle: d.slice(colon + 1).trim() };
  return { evento: d, detalle: '' };
};

  // Edit
  const openEdit = () => {
    if (!repair) return;
    setEditForm({
      client_name: repair.client_name || '', client_phone: repair.client_phone || '',
      contact_phone: repair.contact_phone || '', device_model: repair.device_model || '',
      problem_description: repair.problem_description || '', estimated_cost: String(repair.estimated_cost ?? ''),
      wait_time: repair.wait_time || '',
      branch_id: repair.branch_id || '',
      checklist: normalizeChecklist(repair.checklist),
      unlock_pin: repair.unlock_pin || '',
      unlock_pattern: Array.isArray(repair.unlock_pattern) ? repair.unlock_pattern : [],
      bag: repair.bag != null ? String(repair.bag) : '',
    });
    setBagCheck({ status: 'idle', message: '' });
    setIsEditing(true);
  };

  const checkBagAvailability = (rawBag) => {
    if (bagDebounceRef.current) clearTimeout(bagDebounceRef.current);
    const trimmed = String(rawBag || '').trim();
    // Same value as current → no conflict possible
    if (trimmed === String(repair.bag != null ? repair.bag : '')) {
      setBagCheck({ status: 'idle', message: '' });
      return;
    }
    if (!trimmed) { setBagCheck({ status: 'idle', message: '' }); return; }
    if (!/^\d+$/.test(trimmed) || parseInt(trimmed, 10) < 1) {
      setBagCheck({ status: 'invalid', message: 'Debe ser un numero entero mayor a 0' });
      return;
    }
    setBagCheck({ status: 'checking', message: 'Verificando...' });
    bagDebounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/repairs/check-bag?bag=${trimmed}&exclude_id=${id}`, { headers: getAuthHeaders() });
        if (r.data.available) {
          setBagCheck({ status: 'available', message: `Bolsa #${trimmed} disponible` });
        } else {
          const c = r.data.conflict || {};
          setBagCheck({ status: 'taken', message: `Bolsa #${trimmed} en uso por ${c.ticket || ''} (${c.client_name || ''})` });
        }
      } catch {
        setBagCheck({ status: 'invalid', message: 'Error al verificar' });
      }
    }, 350);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (bagCheck.status === 'taken' || bagCheck.status === 'invalid' || bagCheck.status === 'checking') return;
    try {
      const payload = { ...editForm };
      const bagStr = String(editForm.bag || '').trim();
      payload.bag = bagStr ? parseInt(bagStr, 10) : null;
      const res = await axios.put(`${API}/api/repairs/${id}`, payload, { headers: getAuthHeaders() });
      setRepair(res.data);
      setIsEditing(false);
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const changeCurrentBranch = async (newBranchId) => {
    if (!newBranchId || newBranchId === repair.branch_id) return;
    try {
      const res = await axios.put(`${API}/api/repairs/${id}`, { branch_id: newBranchId }, { headers: getAuthHeaders() });
      setRepair(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo cambiar la sucursal actual');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Cargando...</div>;
  if (!repair) return null;

  const balance = getBalance();

  return (
    <div data-testid="repair-detail-page" className="leading-snug">
      <Link to="/repairs" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1D3C6E] mb-4 transition-colors no-underline">
        <ArrowLeft className="w-4 h-4" /> Volver a reparaciones
      </Link>

      {/* Header — Bolsa is the primary identifier, ticket is secondary */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {repair.bag != null ? (
            <div className="flex flex-col items-center justify-center min-w-[88px] h-[68px] px-3 rounded-xl bg-[#AFBD20] text-[#1D3C6E] shadow-sm" data-testid="repair-bag-display">
              <span className="text-[10px] font-semibold uppercase tracking-wider opacity-80">Bolsa</span>
              <span className="text-3xl font-bold leading-none font-['Outfit']">#{repair.bag}</span>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-w-[88px] h-[68px] px-3 rounded-xl bg-slate-100 text-slate-400 border border-dashed border-slate-300" data-testid="repair-bag-empty">
              <span className="text-[10px] font-semibold uppercase tracking-wider">Bolsa</span>
              <span className="text-sm font-medium">Sin asignar</span>
            </div>
          )}
          {/* Sucursal Origen — immutable, set at creation */}
          {(() => {
            const originId = repair.origin_branch_id || repair.branch_id;
            const originName = (branches.find(b => b && b.id === originId)?.name) || 'Sin sucursal';
            return (
              <div className="flex flex-col items-center justify-center min-w-[88px] h-[68px] px-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-700" data-testid="repair-origin-branch">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Sucursal Origen</span>
                <span className="text-sm font-semibold leading-tight text-center mt-0.5">{originName}</span>
              </div>
            );
          })()}
          {/* Sucursal Actual — editable dropdown that moves the repair */}
          <div className="flex flex-col items-center justify-center min-w-[140px] h-[68px] px-3 rounded-xl bg-[#1D3C6E]/5 border border-[#1D3C6E]/20 text-[#1D3C6E]" data-testid="repair-current-branch">
            <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">Sucursal Actual</span>
            <Select value={repair.branch_id || ''} onValueChange={changeCurrentBranch}>
              <SelectTrigger className="h-7 mt-0.5 border-0 bg-transparent px-1 text-sm font-semibold text-[#1D3C6E] focus:ring-0 focus:ring-offset-0 hover:bg-white/60" data-testid="repair-current-branch-select">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-medium">Ticket</p>
            <h2 className="text-base font-mono font-semibold text-[#1D3C6E] -mt-0.5">{repair.ticket}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{repair.client_name} — {repair.device_model}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={repair.status} onValueChange={updateStatus}>
            <SelectTrigger className="h-9 w-44 text-sm" data-testid="repair-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="print-repair-ticket-btn"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
          <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-repair-btn"><Pencil className="w-4 h-4 mr-1" /> Editar</Button>
        </div>
      </div>

      {statusError && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="status-error">
          <AlertCircle className="w-4 h-4" /> {statusError}
        </div>
      )}

      <div className="space-y-4">
        {/* Top row — 3 columns: Cliente · Equipo · Checklist */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Información del Cliente */}
          <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="repair-client-info">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Información del Cliente</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2"><span className="text-slate-500">Cliente:</span> <span className="font-medium">{repair.client_name}</span></div>
              <div className="col-span-2"><span className="text-slate-500">Teléfono:</span> <span className="font-medium">{repair.client_phone}</span></div>
              {repair.contact_phone && <div className="col-span-2"><span className="text-slate-500">Contacto:</span> <span className="font-medium">{repair.contact_phone}</span></div>}
            </div>
          </div>

          {/* Información del Equipo */}
          <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="repair-device-info">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Información del Equipo</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {repair.device_brand && <div><span className="text-slate-500">Marca:</span> <span className="font-medium">{repair.device_brand}</span></div>}
              <div><span className="text-slate-500">Modelo:</span> <span className="font-medium">{repair.device_model}</span></div>
              {repair.wait_time && <div><span className="text-slate-500">Tiempo espera:</span> <span className="font-medium">{repair.wait_time}</span></div>}
              <div data-testid="repair-days-elapsed">
                <span className="text-slate-500">Días Transcurridos:</span>{' '}
                <span className="font-medium">
                  {(() => {
                    if (!repair.created_at) return '—';
                    const created = new Date(repair.created_at);
                    const today = new Date();
                    const c = new Date(created.getFullYear(), created.getMonth(), created.getDate());
                    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const d = Math.round((t - c) / 86400000);
                    return d === 0 ? '0' : `${d} ${d === 1 ? 'día' : 'días'}`;
                  })()}
                </span>
              </div>
              <div data-testid="repair-registered-by">
                <span className="text-slate-500">Registrado por:</span>{' '}
                <span className="font-medium">{repair.created_by_name || repair.logs?.[0]?.user || repair.created_by || '—'}</span>
              </div>
              {repair.estimated_cost > 0 && <div><span className="text-slate-500">Presupuesto:</span> <span className="font-medium">${n2(repair.estimated_cost)}</span></div>}
              <div className="col-span-2"><span className="text-slate-500">Problema:</span> <span className="font-medium">{repair.problem_description}</span></div>
              {(repair.unlock_pin || (Array.isArray(repair.unlock_pattern) && repair.unlock_pattern.length > 0)) && (
                <div className="col-span-2 mt-2 pt-3 border-t border-slate-100">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Credenciales de Desbloqueo</p>
                  <div className="flex flex-wrap items-start gap-6">
                    {repair.unlock_pin && (
                      <div>
                        <span className="text-slate-500 text-xs">Clave:</span>
                        <p className="font-mono font-semibold tracking-wider text-[#1D3C6E] text-base" data-testid="repair-unlock-pin-view">{repair.unlock_pin}</p>
                      </div>
                    )}
                    {Array.isArray(repair.unlock_pattern) && repair.unlock_pattern.length > 0 && (
                      <div>
                        <span className="text-slate-500 text-xs">Patrón:</span>
                        <div className="mt-1">
                          <PatternPad value={repair.unlock_pattern} readOnly size={120} testid="repair-unlock-pattern-view" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {repair.security_code && (
                <div className="col-span-2 mt-1">
                  <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#1D3C6E]/5 border border-[#1D3C6E]/20 rounded-lg">
                    <Shield className="w-4 h-4 text-[#1D3C6E]" />
                    <span className="text-xs text-slate-500">Codigo de Seguridad:</span>
                    <span className="text-lg font-bold font-mono tracking-widest text-[#1D3C6E]">{repair.security_code}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Checklist de Inspección — 3ra columna del top row */}
          {(() => {
            const cl = normalizeChecklist(repair.checklist);
            const optByValue = Object.fromEntries(CHECKLIST_OPTIONS.map(o => [o.value, o]));
            const rows = checklistItems.length > 0 ? checklistItems : Object.keys(cl);
            // Inline text format: "Item !Respuesta!. Item !Respuesta!. …"
            const parts = rows
              .map((item) => {
                const value = cl[item] || null;
                const label = value ? (optByValue[value]?.shortLabel || value) : '—';
                return `${item} !${label}!`;
              });
            return (
              <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="checklist-view">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Checklist de Inspección</h3>
                {rows.length === 0 ? (
                  <p className="text-xs text-slate-400">Sin items configurados</p>
                ) : (
                  <p className="text-sm text-slate-700 leading-relaxed">{parts.join('. ')}</p>
                )}
              </div>
            );
          })()}
        </div>

        {/* Artículos + Resumen Financiero + Historial de Pagos (sección unificada de ancho completo) */}
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="repair-finance-merged">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sub-sección: Artículos */}
            <div className="lg:col-span-2" data-testid="repair-items-section">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2 font-['Outfit']"><Package className="w-4 h-4 text-[#1D3C6E]" /> Articulos</h3>
              {repair.items?.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {repair.items.map((item, idx) => item && (
                    <div key={item.id || `item-${idx}`} className="flex items-center justify-between py-2 px-3 bg-slate-50 border border-slate-100 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.name}</p>
                        {item.model_compatibility && <p className="text-[10px] text-slate-400">{item.model_compatibility}</p>}
                        <p className="text-xs text-slate-500">{item.quantity} x ${n2(item.price)} = ${n2(item.subtotal)}</p>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        disabled={repair.status === 'delivered' || repair.status === 'archived'}
                        title={(repair.status === 'delivered' || repair.status === 'archived') ? 'No se pueden eliminar artículos en estado Entregado/Archivado' : 'Eliminar artículo'}
                        className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-50"
                        data-testid={`remove-item-${item.id}`}
                      ><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-slate-400 mb-4">No se han agregado articulos</p>}
              {(repair.status === 'delivered' || repair.status === 'archived') ? (
                <div className="flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600" data-testid="add-item-blocked">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
                  <span>
                    No se pueden agregar artículos a una reparación en estado{' '}
                    <strong>{repair.status === 'delivered' ? 'Entregado' : 'Archivado'}</strong>.
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 items-end">
                    <Input data-testid="repair-inventory-search" className="flex-1 text-sm" placeholder="Buscar articulo..." value={inventorySearch}
                      onChange={(e) => setInventorySearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchInventory()} />
                    <Select value={itemSourceBranch || currentBranch || ''} onValueChange={setItemSourceBranch}>
                      <SelectTrigger className="h-9 w-28 text-xs"><SelectValue placeholder="Sucursal" /></SelectTrigger>
                      <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={searchInventory} size="sm" variant="outline"><Search className="w-3.5 h-3.5" /></Button>
                  </div>
                  {inventoryResults.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                      {inventoryResults.map(inv => (
                        <div key={inv.id} onClick={() => addItem(inv)} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 cursor-pointer">
                          <div><p className="text-sm font-medium">{inv.name}</p><p className="text-xs text-slate-500">{inv.type === 'service' ? 'Servicio' : `Stock: ${inv.stock}`} | ${n2(inv.sale_price)}</p></div>
                          <span className="text-[#AFBD20] text-lg">+</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sub-secciones: Resumen + Pagos (columna derecha) */}
            <div className="space-y-6">
              <div data-testid="repair-financial-summary">
                <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2 font-['Outfit']"><DollarSign className="w-4 h-4 text-[#1D3C6E]" /> Resumen Financiero</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Total</span><span className="text-lg font-bold text-slate-900">${n2(repair.total_cost)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Pagado</span><span className="text-lg font-bold text-green-600">${n2(repair.total_paid)}</span></div>
                  <div className="border-t border-slate-200 pt-2 flex justify-between text-sm"><span className="text-slate-500">Saldo</span><span className="text-lg font-bold text-red-600">${n2(balance)}</span></div>
                </div>
                <div className="mt-4 flex gap-2">
                  {balance > 0 ? (
                    <Button onClick={() => { setShowPayment(true); setPaymentAmount(''); }}
                      disabled={!registerOpen}
                      className="flex-1 bg-[#AFBD20] hover:bg-[#9CA819] text-slate-900 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="repair-pay-button">
                      <DollarSign className="w-4 h-4 mr-1" /> Pago / Anticipo
                    </Button>
                  ) : <div className="flex-1 text-center text-sm text-green-600 font-medium py-2">Pagado</div>}
                  {(repair.total_paid || 0) > 0 && (
                    <Button onClick={() => { setShowRefund(true); setRefundAmount(''); setRefundReason(''); }}
                      disabled={!registerOpen}
                      variant="outline"
                      className="text-xs border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="repair-refund-button">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reembolso
                    </Button>
                  )}
                </div>
                {!registerOpen && (balance > 0 || (repair.total_paid || 0) > 0) && (
                  <div className="mt-3 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="register-closed-msg">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>La caja está cerrada. Abre la caja antes de realizar cobros o reembolsos.</span>
                  </div>
                )}
              </div>

              <div data-testid="repair-payments-section">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Historial de Pagos</h3>
                {repair.payments?.length > 0 ? (
                  <div className="space-y-1.5">
                    {repair.payments.map((p, idx) => p && (
                      <div key={p.id || `pay-${idx}`} className={`flex justify-between text-xs py-2 px-2.5 rounded ${p.is_refund ? 'bg-red-50' : 'bg-green-50'}`}>
                        <span className="font-medium">{p.is_refund ? 'Reembolso: ' : ''}${n2(Math.abs(parseFloat(p.amount) || 0))} - {p.method}{p.reason && <span className="text-slate-500 ml-1">({p.reason})</span>}</span>
                        <span className="text-slate-500 text-[10px]">{dateStr(p.date)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400">Sin pagos</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Bitácora + Acciones (sección de ancho completo, abajo del todo) */}
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="repair-logs-section">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Bitácora</h3>
          {Array.isArray(repair.logs) && repair.logs.length > 0 ? (
            <div className="overflow-x-auto max-h-[1000px] overflow-y-auto border border-slate-100 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                    <th className="text-left px-3 py-2 whitespace-nowrap" style={{ width: '1%' }}>Fecha y Hora</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap" style={{ width: '1%' }}>Usuario</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap" style={{ width: '1%' }}>Evento</th>
                    <th className="text-left px-3 py-2">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {[...repair.logs]
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                    .map((log, idx) => {
                      const { evento, detalle } = parseLogEntry(log.description || '');
                      const isNota = evento === 'Nota';
                      const isEditing = isNota && editingLogId === log.id;
                      const saveEdit = async () => {
                        if (!editingLogText.trim()) { setEditingLogId(null); return; }
                        try {
                          await axios.patch(`${API}/api/repairs/${id}/logs/${log.id}`,
                            { description: `Nota: ${editingLogText.trim()}` },
                            { headers: getAuthHeaders() });
                          await fetchRepair();
                        } catch (err) {
                          alert(err.response?.data?.detail || 'No se pudo actualizar la nota');
                        }
                        setEditingLogId(null);
                      };
                      return (
                        <tr key={log.id || `log-${idx}`} className="border-t border-slate-100">
                          <td className="px-3 py-2 whitespace-nowrap text-slate-500">{dateStr(log.created_at)}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-medium text-[#1D3C6E]">{log.user || 'Sistema'}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-700">{evento}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {isEditing ? (
                              <div className="flex items-start gap-2">
                                <textarea
                                  autoFocus
                                  value={editingLogText}
                                  onChange={(e) => setEditingLogText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') setEditingLogId(null);
                                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit();
                                  }}
                                  className="flex-1 border border-slate-200 rounded p-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1D3C6E]/30"
                                  rows={2}
                                  data-testid={`log-edit-textarea-${log.id}`}
                                />
                                <div className="flex flex-col gap-1">
                                  <button type="button" onClick={saveEdit}
                                    className="px-2 py-1 text-[10px] font-medium bg-[#1D3C6E] text-white rounded hover:bg-[#152B50]"
                                    data-testid={`log-edit-save-${log.id}`}>
                                    Guardar
                                  </button>
                                  <button type="button" onClick={() => setEditingLogId(null)}
                                    className="px-2 py-1 text-[10px] font-medium text-slate-500 hover:text-slate-700"
                                    data-testid={`log-edit-cancel-${log.id}`}>
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => isNota ? (setEditingLogId(log.id), setEditingLogText(detalle || '')) : null}
                                disabled={!isNota}
                                title={isNota ? 'Click para editar' : ''}
                                className={`w-full text-left ${isNota ? 'cursor-text hover:bg-amber-50 rounded px-1 -mx-1' : 'cursor-default'}`}
                                data-testid={`log-detalle-${log.id}`}
                              >
                                {detalle || '—'}
                                {log.edited_at && <span className="ml-2 text-[10px] text-slate-400">(editado)</span>}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-slate-400">Sin actividad registrada</p>
          )}
          {/* Acciones (botones placeholder) — abajo de la bitácora */}
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="repair-quick-actions">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { id: 'nota', label: 'Nota', enabled: true },
                { id: 'llamar', label: 'Llamar' },
                { id: 'ya-marque', label: 'Ya marqué' },
                { id: 'solicitar-pieza', label: 'Solicitar Pieza', enabled: true },
                { id: 'whatsapp', label: 'Enviar WhatsApp' },
              ].map(b => (
                <button
                  key={b.id}
                  type="button"
                  disabled={!b.enabled}
                  onClick={
                    b.id === 'nota' ? () => { setNoteText(''); setNoteModalOpen(true); }
                    : b.id === 'solicitar-pieza' ? () => {
                        setPiezaSearch(''); setPiezaSuggestions([]);
                        setPiezaSelected(null); setPiezaQty('1');
                        setPiezaMode('search');
                        setPiezaManual({ name: '', modelo: '', color: '', cantidad: '1' });
                        setPiezaOpen(true);
                      }
                    : undefined
                  }
                  data-testid={`quick-action-${b.id}`}
                  className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    b.enabled
                      ? 'border-[#1D3C6E] bg-[#1D3C6E] text-white hover:bg-[#152B50]'
                      : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 disabled:opacity-70 disabled:cursor-not-allowed'
                  }`}
                  title={b.enabled ? '' : 'Próximamente'}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Note modal — appends a new "Nota: …" log entry */}
      <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="note-modal">
          <DialogHeader><DialogTitle>Agregar Nota</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <textarea
              data-testid="note-textarea"
              autoFocus
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Escribe la nota..."
              className="w-full border border-slate-200 rounded-md p-2.5 text-sm min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#1D3C6E]/30"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNoteModalOpen(false)} data-testid="note-cancel-btn">Cancelar</Button>
              <Button
                className="bg-[#1D3C6E] hover:bg-[#152B50]"
                disabled={!noteText.trim() || noteSaving}
                data-testid="note-save-btn"
                onClick={async () => {
                  if (!noteText.trim()) return;
                  setNoteSaving(true);
                  try {
                    await axios.post(`${API}/api/repairs/${id}/logs`,
                      { description: `Nota: ${noteText.trim()}` },
                      { headers: getAuthHeaders() });
                    setNoteModalOpen(false);
                    setNoteText('');
                    fetchRepair();
                  } catch (err) {
                    alert(err.response?.data?.detail || 'No se pudo guardar la nota');
                  }
                  setNoteSaving(false);
                }}
              >
                {noteSaving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Solicitar Pieza modal — adds to Pedidos cart + logs in bitácora */}
      <Dialog open={piezaOpen} onOpenChange={setPiezaOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="pieza-modal">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Solicitar Pieza</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg text-xs font-medium">
              <button
                type="button"
                onClick={() => setPiezaMode('search')}
                className={`py-1.5 rounded-md transition-colors ${piezaMode === 'search' ? 'bg-white text-[#1D3C6E] shadow-sm' : 'text-slate-500'}`}
                data-testid="pieza-mode-search"
              >Buscar en inventario</button>
              <button
                type="button"
                onClick={() => setPiezaMode('manual')}
                className={`py-1.5 rounded-md transition-colors ${piezaMode === 'manual' ? 'bg-white text-[#1D3C6E] shadow-sm' : 'text-slate-500'}`}
                data-testid="pieza-mode-manual"
              >Agregar manual</button>
            </div>

            {piezaMode === 'search' ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    autoFocus
                    className="pl-10"
                    placeholder="SKU, nombre, modelo..."
                    value={piezaSearch}
                    onChange={(e) => { setPiezaSearch(e.target.value); setPiezaSelected(null); }}
                    data-testid="pieza-search-input"
                  />
                </div>
                {piezaSearch.trim().length >= 2 && !piezaSelected && (
                  <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto" data-testid="pieza-search-results">
                    {piezaSearchLoading && <div className="px-3 py-2 text-xs text-slate-400">Buscando…</div>}
                    {!piezaSearchLoading && piezaSuggestions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-400">Sin resultados</div>
                    )}
                    {piezaSuggestions.map(it => (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => { setPiezaSelected(it); setPiezaSearch(it.name); }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-b-0"
                        data-testid={`pieza-suggestion-${it.id}`}
                      >
                        <span className="font-mono text-xs text-slate-500 w-16 shrink-0">{it.sku}</span>
                        <span className="flex-1 text-sm font-medium text-slate-800 truncate">{it.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {piezaSelected && (
                  <div className="border border-[#AFBD20]/40 bg-[#AFBD20]/5 rounded-md p-3 flex items-center gap-3" data-testid="pieza-selected">
                    <Package className="w-4 h-4 text-[#1D3C6E] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{piezaSelected.name}</p>
                      <p className="font-mono text-[11px] text-slate-500">{piezaSelected.sku || 'sin SKU'}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setPiezaSelected(null); setPiezaSearch(''); }}
                      className="text-slate-400 hover:text-red-500 text-xs"
                    >Quitar</button>
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Cantidad *</label>
                  <Input type="number" min="1" value={piezaQty} onChange={(e) => setPiezaQty(e.target.value)} data-testid="pieza-qty" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Nombre *</label>
                  <Input autoFocus value={piezaManual.name} onChange={(e) => setPiezaManual({ ...piezaManual, name: e.target.value })} data-testid="pieza-manual-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Modelo</label>
                  <Input value={piezaManual.modelo} onChange={(e) => setPiezaManual({ ...piezaManual, modelo: e.target.value })} data-testid="pieza-manual-modelo" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Color</label>
                  <Input value={piezaManual.color} onChange={(e) => setPiezaManual({ ...piezaManual, color: e.target.value })} data-testid="pieza-manual-color" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Cantidad *</label>
                  <Input type="number" min="1" value={piezaManual.cantidad} onChange={(e) => setPiezaManual({ ...piezaManual, cantidad: e.target.value })} data-testid="pieza-manual-cantidad" />
                </div>
              </div>
            )}

            <div className="bg-slate-50 rounded-md p-2.5 text-[11px] text-slate-500">
              La pieza se agregará al bloque <strong>Artículos por Pedir</strong> del módulo Pedidos y quedará registrada en la bitácora de esta reparación.
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setPiezaOpen(false)} data-testid="pieza-cancel-btn">Cancelar</Button>
              <Button
                className="bg-[#1D3C6E] hover:bg-[#152B50]"
                disabled={piezaSaving || (piezaMode === 'search' ? !piezaSelected : !piezaManual.name.trim())}
                onClick={submitSolicitarPieza}
                data-testid="pieza-submit-btn"
              >
                {piezaSaving ? 'Guardando…' : 'Solicitar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Registrar Pago</DialogTitle></DialogHeader>
          <form onSubmit={handlePayment} className="space-y-3">
            <p className="text-sm text-slate-600">Saldo: <span className="font-bold text-red-600">${n2(balance)}</span></p>
            <div><label className="text-xs font-medium text-slate-600">Monto *</label><Input data-testid="repair-payment-amount" type="number" step="0.01" max={balance} value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} required /></div>
            <div><label className="text-xs font-medium text-slate-600">Metodo</label><Select value={paymentMethod} onValueChange={setPaymentMethod}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Efectivo">Efectivo</SelectItem><SelectItem value="Tarjeta">Tarjeta</SelectItem><SelectItem value="Transferencia">Transferencia</SelectItem></SelectContent></Select></div>
            <Button type="submit" className="w-full bg-[#AFBD20] hover:bg-[#9CA819] text-slate-900">Confirmar Pago</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={showRefund} onOpenChange={setShowRefund}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Registrar Reembolso</DialogTitle></DialogHeader>
          <form onSubmit={handleRefund} className="space-y-3">
            <p className="text-sm text-slate-600">Pagado: <span className="font-bold text-green-600">${n2(repair.total_paid)}</span></p>
            <div><label className="text-xs font-medium text-slate-600">Monto *</label><Input type="number" step="0.01" max={parseFloat(repair.total_paid) || 0} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} required /></div>
            <div><label className="text-xs font-medium text-slate-600">Metodo</label><Select value={refundMethod} onValueChange={setRefundMethod}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Efectivo">Efectivo</SelectItem><SelectItem value="Tarjeta">Tarjeta</SelectItem><SelectItem value="Transferencia">Transferencia</SelectItem></SelectContent></Select></div>
            <div><label className="text-xs font-medium text-slate-600">Motivo</label><Input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Razon del reembolso" /></div>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white">Confirmar Reembolso</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Editar Reparacion - {repair.ticket}</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3">
            {/* Bolsa */}
            <div>
              <label className="text-xs font-medium text-slate-600">Bolsa <span className="text-[10px] text-slate-400">(número único entre activas)</span></label>
              <div className="relative">
                <Input
                  data-testid="edit-repair-bag"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Ej: 12"
                  value={editForm.bag || ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    setEditForm({ ...editForm, bag: v });
                    checkBagAvailability(v);
                  }}
                  className={
                    bagCheck.status === 'taken' || bagCheck.status === 'invalid'
                      ? 'border-rose-400 focus-visible:ring-rose-400 pr-9'
                      : bagCheck.status === 'available'
                        ? 'border-emerald-400 focus-visible:ring-emerald-400 pr-9'
                        : 'pr-9'
                  }
                />
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                  {bagCheck.status === 'checking' && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                  {bagCheck.status === 'available' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                  {(bagCheck.status === 'taken' || bagCheck.status === 'invalid') && <AlertCircle className="w-4 h-4 text-rose-500" />}
                </div>
              </div>
              {bagCheck.message && (
                <p className={`text-xs mt-1 ${
                  bagCheck.status === 'available' ? 'text-emerald-600'
                    : bagCheck.status === 'taken' || bagCheck.status === 'invalid' ? 'text-rose-600'
                    : 'text-slate-500'
                }`} data-testid="edit-repair-bag-feedback">
                  {bagCheck.message}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Nombre *</label><Input value={editForm.client_name} onChange={(e) => setEditForm({...editForm, client_name: e.target.value})} required /></div>
              <div><label className="text-xs font-medium text-slate-600">Telefono *</label><Input value={editForm.client_phone} onChange={(e) => setEditForm({...editForm, client_phone: e.target.value})} required /></div>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Telefono contacto</label><Input value={editForm.contact_phone} onChange={(e) => setEditForm({...editForm, contact_phone: e.target.value})} /></div>
            <div><label className="text-xs font-medium text-slate-600">Modelo</label>
              <Select value={editForm.device_model} onValueChange={(v) => setEditForm({...editForm, device_model: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Problema *</label><textarea className="w-full border border-slate-200 rounded-lg p-2 text-sm min-h-[60px]" value={editForm.problem_description} onChange={(e) => setEditForm({...editForm, problem_description: e.target.value})} required /></div>
            <div><label className="text-xs font-medium text-slate-600">Presupuesto</label><Input type="number" step="0.01" value={editForm.estimated_cost} onChange={(e) => setEditForm({...editForm, estimated_cost: e.target.value})} placeholder="$0.00" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Tiempo espera</label>
                <Select value={editForm.wait_time} onValueChange={(v) => setEditForm({...editForm, wait_time: v})}><SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger><SelectContent>{waitTimeOptions.map(wt => <SelectItem key={wt} value={wt}>{wt}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-xs font-medium text-slate-600">Sucursal</label>
                <Select value={editForm.branch_id} onValueChange={(v) => setEditForm({...editForm, branch_id: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            {checklistItems.length > 0 && (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-2 block">Checklist de Inspección</label>
                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="checklist-edit">
                  <div className="grid grid-cols-[1fr_repeat(4,minmax(0,1fr))] gap-px bg-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    <div className="bg-slate-50 px-2 py-1.5">Item</div>
                    {CHECKLIST_OPTIONS.map(opt => (
                      <div key={opt.value} className="bg-slate-50 px-1 py-1.5 text-center">{opt.shortLabel}</div>
                    ))}
                  </div>
                  <div className="max-h-72 overflow-y-auto bg-white">
                    {checklistItems.map((item, idx) => {
                      const value = editForm.checklist?.[item] || null;
                      return (
                        <div key={idx} className="grid grid-cols-[1fr_repeat(4,minmax(0,1fr))] gap-px bg-slate-100 border-t border-slate-100 first:border-t-0">
                          <div className="bg-white px-2 py-2 text-sm text-slate-700">{item}</div>
                          {CHECKLIST_OPTIONS.map(opt => {
                            const active = value === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setEditForm(prev => ({
                                  ...prev,
                                  // Single-click toggles same value off; otherwise selects
                                  checklist: { ...prev.checklist, [item]: active ? null : opt.value },
                                }))}
                                className={`bg-white py-1.5 px-1 text-[11px] font-medium border-2 transition-colors ${
                                  active ? opt.activeCls : 'border-transparent text-slate-400 hover:bg-slate-50'
                                }`}
                                aria-label={`${item}: ${opt.label}`}
                                data-testid={`checklist-${idx}-${opt.value}`}
                                title={opt.label}
                              >
                                {active ? '●' : '○'}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Credenciales de Desbloqueo (editable) */}
            <div className="border border-slate-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">Credenciales de Desbloqueo</p>
              <p className="text-[11px] text-slate-500 mb-3">Ambos campos son opcionales.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Clave</label>
                  <Input
                    data-testid="edit-unlock-pin"
                    value={editForm.unlock_pin || ''}
                    onChange={(e) => setEditForm({ ...editForm, unlock_pin: e.target.value })}
                    placeholder="PIN o contraseña"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Patrón</label>
                  <PatternPad
                    value={editForm.unlock_pattern || []}
                    onChange={(p) => setEditForm({ ...editForm, unlock_pattern: p })}
                    testid="edit-unlock-pattern"
                    size={160}
                  />
                </div>
              </div>
            </div>

            <Button type="submit" disabled={bagCheck.status === 'taken' || bagCheck.status === 'invalid' || bagCheck.status === 'checking'} className="w-full bg-[#1D3C6E] hover:bg-[#152B50] disabled:opacity-50 disabled:cursor-not-allowed">Guardar Cambios</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delivery Verification Dialog */}
      <Dialog open={showDeliveryVerify} onOpenChange={setShowDeliveryVerify}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Verificar Entrega</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Method selector */}
            <div className="grid grid-cols-2 gap-2" role="tablist">
              <button
                type="button"
                role="tab"
                data-testid="delivery-method-code"
                onClick={() => { setDeliveryMethod('security_code'); setDeliveryError(''); }}
                className={`flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-lg border text-xs font-medium transition-colors ${
                  deliveryMethod === 'security_code'
                    ? 'border-[#1D3C6E] bg-[#1D3C6E]/5 text-[#1D3C6E]'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Shield className="w-5 h-5" />
                Código de reclamación
              </button>
              <button
                type="button"
                role="tab"
                data-testid="delivery-method-id"
                onClick={() => { setDeliveryMethod('id_document'); setDeliveryError(''); }}
                className={`flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-lg border text-xs font-medium transition-colors ${
                  deliveryMethod === 'id_document'
                    ? 'border-[#1D3C6E] bg-[#1D3C6E]/5 text-[#1D3C6E]'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
              >
                <IdCard className="w-5 h-5" />
                Identificación
              </button>
            </div>

            {deliveryMethod === 'security_code' ? (
              <>
                <p className="text-xs text-slate-500 text-center">
                  Ingresa el código de 6 dígitos que el cliente recibió al registrar la reparación.
                </p>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Código de Seguridad</label>
                  <Input
                    data-testid="delivery-security-code"
                    className="text-center text-2xl font-mono font-bold tracking-[0.3em] h-12"
                    value={securityCodeInput}
                    onChange={(e) => setSecurityCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && securityCodeInput.length === 6 && confirmDelivery()}
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-slate-500">
                  Si el cliente no tiene el código, captura sus datos de identificación oficial. Los tres campos son obligatorios.
                </p>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Nombre *</label>
                    <Input
                      data-testid="delivery-id-name"
                      value={idName}
                      onChange={(e) => setIdName(e.target.value)}
                      placeholder="Nombre(s)"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Apellido *</label>
                    <Input
                      data-testid="delivery-id-last-name"
                      value={idLastName}
                      onChange={(e) => setIdLastName(e.target.value)}
                      placeholder="Apellido(s)"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Número de identificación *</label>
                    <Input
                      data-testid="delivery-id-number"
                      value={idNumber}
                      onChange={(e) => setIdNumber(e.target.value)}
                      placeholder="INE, pasaporte, etc."
                    />
                  </div>
                </div>
              </>
            )}

            {deliveryError && (
              <div className="flex items-start gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700" data-testid="delivery-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{deliveryError}</span>
              </div>
            )}

            <Button
              onClick={confirmDelivery}
              disabled={
                deliveryMethod === 'security_code'
                  ? securityCodeInput.length !== 6
                  : !idName.trim() || !idLastName.trim() || !idNumber.trim()
              }
              className="w-full bg-[#1D3C6E] hover:bg-[#152B50] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="confirm-delivery-btn"
            >
              Confirmar Entrega
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden thermal ticket — only visible when printing. The page-level
          error boundary will catch any render error here. */}
      <ThermalTicket
        variant="repair"
        data={repair}
        branchName={(branches || []).find(b => b && b.id === repair.branch_id)?.name || ''}
      />
    </div>
  );
}

export default function RepairDetail() {
  return (
    <RepairDetailBoundary>
      <RepairDetailInner />
    </RepairDetailBoundary>
  );
}
